import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { cn } from 'src/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { formatScheduleForDisplay, formatSchedulerMutationError } from 'src/helpers/scheduler-schedule.helpers';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useRunScheduledTaskNowMutation } from 'src/redux/operations/operations.endpoints';
import type { ScheduledTask } from '../types/scheduler.types';
import { SchedulerRunNowDialog } from './scheduler-run-now-dialog';
import { SchedulerDsButton } from './scheduler-ds-button';
import { SchedulerStatusBadge } from './scheduler-status-badge';

interface SchedulerTaskListProps {
  tasks: ScheduledTask[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onSelect: (task: ScheduledTask) => void;
}

export function SchedulerTaskList({
  tasks,
  isLoading,
  isError,
  onSelect,
}: SchedulerTaskListProps) {
  const dispatch = useDispatch();
  const [runNowTask, setRunNowTask] = useState<ScheduledTask | null>(null);
  const [runNow, { isLoading: isRunningNow }] = useRunScheduledTaskNowMutation();

  const handleConfirmRunNow = async() => {
    if (!runNowTask) {
      return;
    }
    try {
      await runNow(runNowTask.id).unwrap();
      dispatch(
        showNotification({
          summary: `Queued manual run for "${runNowTask.name}"`,
          theme: 'success',
        }),
      );
      setRunNowTask(null);
    } catch (error) {
      dispatch(
        showNotification({
          summary: formatSchedulerMutationError(error),
          theme: 'warning',
        }),
      );
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 py-8 text-sm text-gray-500"
        role="status"
      >
        <Loader2 className="size-4 animate-spin text-gray-500" />
        Loading scheduled tasks…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700"
        role="alert"
      >
        Could not load scheduled tasks.
      </div>
    );
  }

  if (!tasks?.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">
        No scheduled tasks yet. Use
        {' '}
        <strong className="font-medium text-black">
          New task
        </strong>
        {' '}
        to add one.
      </p>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-md border border-gray-400">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-400 bg-gray-200 hover:bg-gray-200">
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Name
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Schedule
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Enabled
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Last run
              </TableHead>
              <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                Status
              </TableHead>
              <TableHead className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
              <TableRow
                key={task.id}
                className={cn(
                  'cursor-pointer border-b border-gray-300 transition-colors',
                  'hover:bg-gray-200/80 focus-visible:bg-gray-200/80',
                  !task.is_enabled && 'opacity-60'
                )}
                onClick={() => onSelect(task)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(task);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${task.name}`}
              >
                <TableCell className="px-4 py-3 font-medium text-black">
                  {task.name}
                </TableCell>
                <TableCell className="px-4 py-3 font-mono text-xs text-gray-700">
                  {formatScheduleForDisplay(task)}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-gray-700">
                  {task.is_enabled ? 'Yes' : 'No'}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-gray-700">
                  {task.last_run_at ? formatDisplayDateTime(task.last_run_at) : '—'}
                </TableCell>
                <TableCell className="px-4 py-3">
                  <SchedulerStatusBadge status={task.last_status} />
                </TableCell>
                <TableCell className="px-4 py-3 text-right">
                  <SchedulerDsButton
                    type="button"
                    variant="accent"
                    size="sm"
                    disabled={!task.is_enabled}
                    aria-label={`Run ${task.name} now`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setRunNowTask(task);
                    }}
                  >
                    Run Now
                  </SchedulerDsButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <SchedulerRunNowDialog
        task={runNowTask}
        open={runNowTask !== null}
        isRunning={isRunningNow}
        onOpenChange={(open) => {
          if (!open) {
            setRunNowTask(null);
          }
        }}
        onConfirm={handleConfirmRunNow}
      />
    </>
  );
}
